import React from 'react';
import { FaCreditCard, FaSpinner } from 'react-icons/fa';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild, Button } from '@headlessui/react';
import { Fragment } from 'react';

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  onPayWithStripe, 
  onSkipPayment,
  onCancel,
  isLoading,
  productInfo,
  items = [],
  userEmail
}) => {
  if (!isOpen) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-black border border-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="text-center mb-6">
                  <FaCreditCard className="text-4xl text-white mx-auto mb-4" />
                  <DialogTitle as="h3" className="text-2xl font-bold text-white mb-2">
                    Complete Payment
                  </DialogTitle>
                  {onSkipPayment && (
                    <p className="text-sm text-gray-400 mb-2">
                      💡 Development Mode: Use Test Payment to skip payment
                    </p>
                  )}
                </div>
        
                <div className="space-y-4 mb-6">
                  <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                    {items.length > 0 ? (
                      <>
                        <h4 className="font-semibold mb-2 text-white">Selected Items</h4>
                        <ul className="space-y-1 text-sm">
                          {items.map((item, index) => (
                            <li key={index} className="flex justify-between text-gray-300">
                              <span>{item.title}</span>
                              <span className="font-medium">${item.price}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <>
                        <h4 className="font-semibold mb-2 text-white">Product Information</h4>
                        <div className="flex justify-between items-center text-gray-300">
                          <span>{productInfo?.name || 'Document Generation'}</span>
                          <span className="font-medium">${productInfo?.price || 0}</span>
                        </div>
                        {productInfo?.description && (
                          <p className="text-sm text-gray-400 mt-2">{productInfo.description}</p>
                        )}
                      </>
                    )}
                    
                    {/* Total Amount */}
                    <div className="mt-6 flex flex-col items-center">
                      <span className="text-lg text-gray-300 font-semibold mb-1">Total Amount</span>
                      <span className="text-4xl font-extrabold text-white">
                        ${productInfo?.price || items.reduce((sum, item) => sum + item.price, 0)}
                      </span>
                    </div>
                  </div>
                </div>
        
                <div className="space-y-3">
                  {/* Pay with Stripe Button */}
                  <Button
                    onClick={onPayWithStripe}
                    disabled={isLoading}
                    className="w-full px-4 py-3 bg-white text-black font-semibold rounded-xl hover:bg-gray-100 transition-colors duration-200 data-hover:bg-gray-100 data-focus:ring-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <FaSpinner className="mr-2 animate-spin" />
                        Processing Payment...
                      </>
                    ) : (
                      <>
                        <FaCreditCard className="mr-2" />
                        Pay with Stripe
                      </>
                    )}
                  </Button>
                  
                  {/* Test Payment Button */}
                  {onSkipPayment && (
                    <Button
                      onClick={onSkipPayment}
                      disabled={isLoading}
                      className="w-full px-4 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors duration-200 data-hover:bg-green-700 data-focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isLoading ? (
                        <>
                          <FaSpinner className="mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          🧪 Test Payment (Skip)
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Cancel Button */}
                  <Button
                    onClick={onCancel}
                    disabled={isLoading}
                    className="w-full px-4 py-2 border border-gray-600 text-gray-400 font-medium rounded-xl hover:bg-gray-800 transition-colors duration-200 data-hover:bg-gray-800 data-focus:ring-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    Cancel
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PaymentModal;